import { PackagePrice, ProductImageType } from "@/models/GSDD";
import { DrugCategory } from "@/models/DrugCategory";
import { VendorDrugMap } from "@/models/VendorDrugMap";
import { DeliveryCode } from "@/models/DeliveryCode";
import HasLabel from "@/models/HasLabel";
import TextUtils from "@/util/TextUtils";
import { Program } from "@/models/Program";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";

export interface DrugStatus {
    packageID: number;
    status: DrugStatus.DrugStatusEnum;
    disableAutorefill: boolean;
    disableMedSync?: boolean;
    disableTextAndFaxNotification: number;
    updated?: Date;
    updatedBy?: string;
    overrideName?: string;
    minimumDispenseQuantityOverride?: number;
}

export class DrugStatus {
    constructor(obj?: DrugStatus) {
        this.status = 0;
        this.disableAutorefill = false;
        this.disableTextAndFaxNotification = 0;

        if (obj) Object.assign(this, obj);
    }
}

export namespace DrugStatus {
    export enum DrugStatusEnum {
        Inactive,
        Active,
        ActiveAndPreferred,
        BlockedFromFills,
    }
}

export interface Drug extends DrugProduct {
    packageID?: number | null;
    productID: string;
    billingUnit: string;
    productNameLong: string;
    marketer: string;
    doseForm: string;
    deaClass: string;
    brandGenericStatus: string;
    isOffMarket: boolean;
    isReplaced: boolean;
    autosend: boolean;
    packageDescription: string;
    therapeuticEquivalenceCode: string[];
    routeOfAdministration: string | null;
    activeStatus: DrugStatus.DrugStatusEnum;

    productDetail: any; // GSDD DetailProductType
    packageDetail: any; // GSDD DetailPackageType
    prices: PackagePrice[]; // GSDD PriceDataType
    images: ProductImageType[];

    notes: DrugNote[];
    status?: DrugStatus;

    categories?: DrugCategory[];
    vendors?: VendorDrugMap[];
    minimumDispenseQuantity?: number;
}

const statusEmojiMap : Record<DrugStatus.DrugStatusEnum, string> = {
    [DrugStatus.DrugStatusEnum.ActiveAndPreferred]: '⭐',
    [DrugStatus.DrugStatusEnum.Active]: '🟢',
    [DrugStatus.DrugStatusEnum.BlockedFromFills]: '🔴',
    [DrugStatus.DrugStatusEnum.Inactive]: '⚫'
};

export class Drug extends DrugProduct {
    constructor(packageId?: number | null, obj?: Drug | DrugProduct) {
        super(obj);
        this.packageID = packageId;

        if (packageId != null) this.drugId = Number(packageId);

        this.status = new DrugStatus();
        this.source = DrugSource.GsddFederal;

        if (obj) {
            Object.assign(this, obj);
            this.packageID = obj.drugId;
            if (obj instanceof Drug && obj.status) this.status = new DrugStatus(obj.status);

            if (this.source != DrugSource.GsddFederal) {
                this.status.status = Number(obj.activeStatus);
                this.status.disableAutorefill = true;
                this.status.packageID = Number(this.drugId);
            }
        }
    }

    toString(long: boolean = false): string {
        // Use override name if it exists, otherwise fall back to product names
        let productName = this.overrideName || this.status?.overrideName || this.productNameShort || this.productNameLong;
        let display = '';

        if (long && !this.overrideName && !this.status?.overrideName) {
            productName = this.productNameLong || this.productNameShort;
        }

        const status = statusEmojiMap[this.activeStatus];

        if (productName) {
            display = productName;
            if (this.ndc) {
                display = '';
                if (this.source == DrugSource.NonGsddProduct) display = '(*) ';

                display += `${status} NDC:${this.ndc} - ${productName} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}`;
            }
        }
        if (this.quantityOnHand) {
            display += `${this.quantityOnHand ? ' - QTY: ' + this.quantityOnHand : ''}`;
        }

        if (display) return display;

        return '<div class="font-weight-light">Name/NDC</div>';
    }

    get id(): number {
        return this.drugId;
    }

    get isCII(): boolean {
        return this.deaClass == "schedule II";
    }

    get isControl(): boolean {
        if (this.deaClass) {
            return this.deaClass.startsWith("schedule");
        } else {
            return false;
        }
    }

    get NDCFormat542(): string {
        return Drug.formatNDC542(this.ndc);
    }

    static formatNDC542(value: string | null): string {
        if (!value) return '';
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    }

    formatSearchOptions(objects : Array<any>) : Array<any> {
        if (!objects?.length) return objects;

        const preferred = objects.filter(o => o.activeStatus == DrugStatus.DrugStatusEnum.ActiveAndPreferred);
        const active = objects.filter(o => o.activeStatus == DrugStatus.DrugStatusEnum.Active);
        const inactive = objects.filter(o => o.activeStatus == DrugStatus.DrugStatusEnum.Inactive);
        const blocked = objects.filter(o => o.activeStatus == DrugStatus.DrugStatusEnum.BlockedFromFills);

        return [
            { heading: true, label: `${statusEmojiMap[DrugStatus.DrugStatusEnum.ActiveAndPreferred]} Preferred`, visible: !!preferred.length },
            ...preferred,
            { heading: true, label: `${statusEmojiMap[DrugStatus.DrugStatusEnum.Active]} Active`, visible: !!active.length },
            ...active,
            { heading: true, label: `${statusEmojiMap[DrugStatus.DrugStatusEnum.Inactive]} Inactive`, visible: !!inactive.length },
            ...inactive,
            { heading: true, label: `${statusEmojiMap[DrugStatus.DrugStatusEnum.BlockedFromFills]} Blocked from Fills`, visible: !!blocked.length },
            ...blocked
        ].filter(o => o.visible !== false);
    }
}

export namespace Drug {
    export class EquivalentDrug {
        packageID?: number;
        productID?: number;
        ndc?: string;
        brandGenericStatus?: string;
        productNameLong?: string;
        marketer?: string;
        packageSize?: number;
        activeStatus?: DrugStatus.DrugStatusEnum;
        onHand?: number;
        lastPurchasePrice?: number;
        lastPurchaseDate?: Date;
        averageCost90Days?: number;
    }
}

export interface DrugNote {
    packageID: number;
    note: string;
    created: Date;
    createdBy: string;
}

export interface DrugProgram {
    packageID: number;
    drugSource: DrugSource;
    programID: number;
    deliveryCode: DeliveryCode | null;
    autoselect: boolean;
    awaitsPriorAuth: boolean;
    disableAutorefill: boolean;
    program: Program;

    created: Date;
    createdBy: string;

    drugName: string;
    ndc: string;
    autoselectProgram: Program | null;

    // only used for order audit configuration, not actually stored on this object.
    orderAuditId: number | null;
    minimumMargin: number | null;
    maximumMargin: number | null;
    maximumCopay: number | null;
}

export class DrugProgram extends HasLabel {
    constructor(obj?: DrugProgram) {
        super();
        this.deliveryCode = null;
        this.autoselect = false;

        if (obj) {
            Object.assign(this, obj);
            this.program = new Program(obj.program);
            this.autoselectProgram = new Program(obj.autoselectProgram || undefined);
        }
    }

    get deliveryTypeDesc(): string {
        if (this.deliveryCode == null) return '';

        const errorDesc = DeliveryCode[(this.deliveryCode)];
        return TextUtils.camelCaseToNormal(errorDesc);
    }

    set drug(val: DrugProduct) {
        this.packageID = Number(val.drugId);
        this.drugSource = val.source;
        this.drugName = val.productNameShort;
        this.ndc = val.ndc;
    }

    toString(): string {
        return `${this.ndc} - ${this.drugName} - ${this.deliveryTypeDesc || 'No delivery method'}`;
    }
}
